import type { DeviceSpecDevice, DeviceSpecTag, EdgeDevice, EdgeDeviceChannel, EdgeTag } from '../../types'
import { flattenEdgeChannelInfo } from './channelShape'
import { channelDef } from './catalog'
import { emptyDevice, newKey, normalizeAddress } from './model'
import type { FormulaField } from './recompute'

// Edge → 명세 가져오기 (현장 우선). Edge 에 실제로 설정된 값으로 명세를 맞추되, 명세에만 있는 칸은 남긴다.
//  · Edge 값으로: 디바이스 이름·설명·프로토콜·채널 설정, 태그 크기·타입·연산·Alias
//    (Alias 는 Edge 값이 비었으면 명세 값을 남긴다 — 구버전 Edge 는 Alias 를 보내지 않는다)
//  · 남김: 설비구분·제조사·모델·메모, 태그 설명·항목명 (명세 전용 — Edge 태그 Description 은 메모라 가져오지 않는다)
//  · 수식: 계산값이 Edge 값과 같으면 수식을 남기고, 다르면 Edge 값으로 바꾸고 수식을 뗀다
//  · 태그는 주소로 짝짓는다 (대소문자·띄어쓰기·숫자 앞 0 무시 — D02000 = d2000, "1, 3x0" = 1-3x0). 짝이 맞으면 Edge 값으로 덮되
//    주소는 명세 표기를 남긴다 (같은 주소이므로). 짝이 없는 Edge 태그는 Edge 표기로 들어온다.
//    Edge 에 없는 명세 태그는 keepSpecOnly 면 원래 자리 근처에 남기고, 아니면 뺀다
//    (아직 Edge 에 안 넣은 새 태그가 날아가지 않게 기본은 남기기)
//  · 미리보기·비교는 specCompare (명세 → Edge 적용과 같은 비교)

// 짝짓기용 주소 — 같은 주소를 다르게 적은 것(대소문자·띄어쓰기·숫자 앞 0, Modbus 구분자)을 같게 본다
export function addressKey(channelType: string, address: string): string {
  return normalizeAddress(channelDef(channelType), address ?? '')
    .toUpperCase().replace(/\s+/g, '').replace(/\d+/g, n => n.replace(/^0+(?=\d)/, ''))
}

/** Edge channelInfo JSON → 명세 채널 값 (선택지 "…Pairs"·중첩 객체는 빼고 문자열만) */
export function channelFromEdge(json: string): Record<string, string> {
  let parsed: unknown
  try { parsed = JSON.parse(json) } catch { return {} }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
  // Custom 은 value 안에 접속 칸이 들어 있다 — 펴서 담는다
  return Object.fromEntries(Object.entries(flattenEdgeChannelInfo(parsed as Record<string, unknown>))
    .filter(([k, v]) => !k.endsWith('Pairs') && (v == null || typeof v !== 'object'))
    .map(([k, v]) => [k, v == null ? '' : String(v)]))
}

// Edge 태그마다 짝지어진 명세 태그 (없으면 undefined) — 같은 주소가 둘 이상이면 앞의 것끼리 (하나에 하나)
export function pairTags(channelType: string, edgeTags: EdgeTag[], specTags: DeviceSpecTag[]): (DeviceSpecTag | undefined)[] {
  const key = (a: string) => addressKey(channelType, a)
  const byAddress = new Map<string, DeviceSpecTag>()
  for (const t of specTags) if (!byAddress.has(key(t.address))) byAddress.set(key(t.address), t)
  const used = new Set<string>()
  return edgeTags.map(e => {
    const candidate = byAddress.get(key(e.address ?? ''))
    if (!candidate || used.has(candidate.key)) return undefined
    used.add(candidate.key)
    return candidate
  })
}

export function mergeEdgeDevice(edge: EdgeDevice, channel: EdgeDeviceChannel, existing: DeviceSpecDevice | undefined, keepSpecOnly: boolean): DeviceSpecDevice {
  const base = existing ?? emptyDevice()
  const olds = pairTags(channel.channelType, edge.tags, existing?.tags ?? [])
  const used = new Set(olds.flatMap(o => (o ? [o.key] : [])))
  const merged: DeviceSpecTag[] = edge.tags.map((e, i) => {
    const old = olds[i]
    const next: DeviceSpecTag = {
      key: old?.key ?? newKey(),
      name: old?.name ?? '',
      description: old?.description ?? '',
      address: old?.address ?? e.address ?? '',
      size: String(e.size),
      dataType: e.dataType,
      arithmetic: e.arithmetic ?? '',
      alias: e.alias?.trim() ? e.alias.trim() : old?.alias ?? '',
    }
    // 수식은 계산값이 그대로일 때만 남긴다
    if (old?.formulas) {
      const formulas: DeviceSpecTag['formulas'] = {}
      for (const [field, formula] of Object.entries(old.formulas) as [FormulaField, string][]) {
        if (formula && old[field] === next[field]) formulas[field] = formula
      }
      if (Object.keys(formulas).length > 0) next.formulas = formulas
    }
    return next
  })
  // 짝지어진(덮어쓴) 명세 태그는 빼고, 남은 명세 전용 태그만 남기기 대상
  const tags = keepSpecOnly && existing ? withSpecOnly(merged, existing.tags, used) : merged
  return {
    ...base,
    code: edge.code,
    name: edge.name,
    description: edge.description ?? '',
    channelType: channel.channelType,
    channel: pulledChannel(channel, base),
    tags,
  }
}

// Edge 는 비밀번호를 비워 보낸다 — 명세에 있던 비밀번호는 지키고, 비어 있으면 Edge 에 보낼 때도 기존 값이 유지된다
function pulledChannel(channel: EdgeDeviceChannel, base: DeviceSpecDevice): Record<string, string> {
  const channelValues = channelFromEdge(channel.channelInfoJson)
  for (const key of Object.keys(channelValues)) {
    if (key.toLowerCase() === 'password' && !channelValues[key] && base.channel[key]) channelValues[key] = base.channel[key]
  }
  return channelValues
}

// 명세에만 있는 태그를 명세에서 바로 앞에 있던(Edge 와 짝지어진) 태그 뒤에 끼운다 — 앞에 아무것도 없으면 맨 앞
function withSpecOnly(merged: DeviceSpecTag[], specTags: DeviceSpecTag[], matched: Set<string>): DeviceSpecTag[] {
  const after = new Map<string | null, DeviceSpecTag[]>()
  let anchor: string | null = null
  for (const t of specTags) {
    if (matched.has(t.key)) { anchor = t.key; continue }
    after.set(anchor, [...(after.get(anchor) ?? []), t])
  }
  const out = [...(after.get(null) ?? [])]
  for (const t of merged) out.push(t, ...(after.get(t.key) ?? []))
  return out
}
